import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  FiUsers,
  FiUserCheck,
  FiLayers,
  FiDollarSign,
  FiRefreshCcw,
  FiSearch,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import './analytics.css';
import ClientPagination, { useClientPagination } from '../../components/common/ClientPagination';

const COLORS = ["#7A2238", "#D4AF37", "#5A182A", "#A94D6D", "#2563EB"];

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const number = (value) => Number(value || 0).toLocaleString("en-IN");

const dateLabel = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
      }).format(new Date(`${value}T00:00:00.000Z`))
    : "";

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "—";

function comparison(value) {
  if (value === null || value === undefined) {
    return { text: "No data last year", color: "text-gray-400" };
  }

  const amount = Number(value);

  return {
    text: `${amount >= 0 ? "+" : ""}${amount.toFixed(1)}% vs last year`,
    color: amount >= 0 ? "text-green-600" : "text-red-600",
  };
}

export default function EmployeeReport() {
  const { accessToken } = useAuth();

  const [departmentId, setDepartmentId] = useState("");
  const [employeeStatus, setEmployeeStatus] = useState("");
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");

  const [report, setReport] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const range = useMemo(() => {
    if (!month) return {};

    const [year, monthNumber] = month.split("-").map(Number);
    const from = `${month}-01`;
    const to = new Date(Date.UTC(year, monthNumber, 0))
      .toISOString()
      .slice(0, 10);

    return { from, to };
  }, [month]);

  const loadDepartments = useCallback(async () => {
    if (!accessToken) return;

    try {
      const response = await apiRequest("/departments", { accessToken });
      setDepartments(response || []);
    } catch {
      setDepartments([]);
    }
  }, [accessToken]);

  const loadReport = useCallback(
    async (refresh = false) => {
      if (!accessToken) return;

      try {
        refresh ? setRefreshing(true) : setLoading(true);
        setError("");

        const query = new URLSearchParams();

        if (range.from) query.set("from", range.from);
        if (range.to) query.set("to", range.to);
        if (departmentId) query.set("departmentId", departmentId);
        if (employeeStatus) query.set("employeeStatus", employeeStatus);

        const response = await apiRequest(
          `/reports/employees?${query.toString()}`,
          { accessToken },
        );

        setReport(response);
      } catch (requestError) {
        setError(requestError.message || "Unable to load employee report.");
        setReport(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [accessToken, range.from, range.to, departmentId, employeeStatus],
  );

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const metrics = report?.metrics || {};
  const changes = report?.comparison?.changes || {};

  const growthData = useMemo(() => {
    const previous = report?.previousEmployeeGrowth || [];

    return (report?.employeeGrowth || []).map((item, index) => ({
      label: dateLabel(item.date),
      thisYear: Number(item.employees || 0),
      lastYear: Number(previous[index]?.employees || 0),
    }));
  }, [report]);

  const employees = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (report?.employees || []).filter((employee) => {
      if (!query) return true;

      return (
        employee.name?.toLowerCase().includes(query) ||
        employee.employeeCode?.toLowerCase().includes(query) ||
        employee.department?.toLowerCase().includes(query) ||
        employee.designation?.toLowerCase().includes(query)
      );
    });
  }, [report?.employees, search]);

  const cards = [
    {
      title: "Total Employees",
      value: number(metrics.totalEmployees),
      subtitle: comparison(changes.totalEmployees),
      icon: <FiUsers size={22} />,
      iconBg: "bg-primary-50 text-primary-700",
    },
    {
      title: "Active Employees",
      value: number(metrics.activeEmployees),
      subtitle: { text: "Currently active", color: "text-green-600" },
      icon: <FiUserCheck size={22} />,
      iconBg: "bg-green-50 text-green-600",
    },
    {
      title: "Departments",
      value: number(metrics.departments),
      subtitle: { text: "Across company", color: "text-blue-600" },
      icon: <FiLayers size={22} />,
      iconBg: "bg-blue-50 text-blue-600",
    },
    {
      title: "Average Salary",
      value: money(metrics.averageSalary),
      subtitle: { text: "Per employee", color: "text-yellow-600" },
      icon: <FiDollarSign size={22} />,
      iconBg: "bg-yellow-50 text-yellow-600",
    },
  ];


  const pagination = useClientPagination(employees);
  return (
    <div className="charms-analytics space-y-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Analytics</h1>
          <p className="mt-2 text-gray-500">
            Workforce, department and compensation analytics.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => loadReport(true)}
            disabled={refreshing}
            className="flex items-center gap-2 rounded-2xl border bg-white px-5 py-3 disabled:opacity-50"
          >
            <FiRefreshCcw className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>

        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <select
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
            className="rounded-2xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="">All Departments</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>

          <select
            value={employeeStatus}
            onChange={(event) => setEmployeeStatus(event.target.value)}
            className="rounded-2xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="DRAFT">Draft</option>
            <option value="INACTIVE">Inactive</option>
            <option value="EXITED">Exited</option>
          </select>

          <input
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="rounded-2xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      {loading ? (
        <div className="rounded-3xl bg-white py-24 text-center text-gray-500">
          Loading employee report...
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            {cards.map((card) => (
              <div key={card.title} className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{card.title}</p>
                    <h2 className="mt-2 text-3xl font-bold">{card.value}</h2>
                    <p className={`mt-2 text-xs font-semibold ${card.subtitle.color}`}>
                      {card.subtitle.text}
                    </p>
                  </div>
                  <div className={`rounded-2xl p-4 ${card.iconBg}`}>
                    {card.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <section className="xl:col-span-2 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold">Employee Joining Comparison</h2>
              <p className="mb-5 text-sm text-gray-500">
                Selected period compared with the same dates last year.
              </p>

              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Legend />

                  <Area
                    type="monotone"
                    dataKey="thisYear"
                    name="This Year"
                    stroke="#7A2238"
                    fill="#7A2238"
                    fillOpacity={0.22}
                  />

                  <Area
                    type="monotone"
                    dataKey="lastYear"
                    name="Last Year"
                    stroke="#D4AF37"
                    fill="#D4AF37"
                    fillOpacity={0.12}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </section>

            <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-xl font-bold">Department Distribution</h2>

              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={report.departmentDistribution || []}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={110}
                  >
                    {(report.departmentDistribution || []).map((item, index) => (
                      <Cell
                        key={`${item.name}-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>

                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </section>
          </div>

          <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
            <h2 className="mb-5 text-xl font-bold">Employment Type Distribution</h2>

            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={report.employmentTypeDistribution || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="employees" fill="#7A2238" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </section>

          <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold">Employee List</h2>
                <p className="text-gray-500">Live workforce details</p>
              </div>

              <div className="relative w-full lg:w-80">
                <FiSearch className="absolute left-4 top-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search employee..."
                  className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {["Employee", "Department", "Designation", "Joining Date", "Salary", "Status"].map((heading) => (
                      <th key={heading} className="px-4 py-3 text-left text-sm">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {pagination.pageItems.map((employee) => (
                    <tr key={employee.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <p className="font-semibold">{employee.name}</p>
                        <p className="text-sm text-gray-500">{employee.employeeCode}</p>
                      </td>
                      <td className="px-4 py-4">{employee.department}</td>
                      <td className="px-4 py-4">{employee.designation}</td>
                      <td className="px-4 py-4">{formatDate(employee.joiningDate)}</td>
                      <td className="px-4 py-4">{money(employee.salary)}</td>
                      <td className="px-4 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          employee.status === "ACTIVE"
                            ? "bg-green-100 text-green-700"
                            : employee.status === "DRAFT"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                        }`}>
                          {employee.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ClientPagination pagination={pagination} label="employees" />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
